import { useCallback, useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { Content, Table, TableColumn, WarningPanel } from '@backstage/core-components';
import { Button, Chip, makeStyles } from '@material-ui/core';
import { ManagedClusterDetailPanel } from './ManagedClusterDetailPanel';

const useStyles = makeStyles(theme => ({
  provisioned:  { backgroundColor: '#228722', color: '#fff', height: 22, fontSize: '0.75rem' },
  provisioning: { backgroundColor: '#527EDB', color: '#fff', height: 22, fontSize: '0.75rem' },
  failed:       { backgroundColor: '#cd3c14', color: '#fff', height: 22, fontSize: '0.75rem' },
  deleting:     { backgroundColor: '#cd3c14', color: '#fff', height: 22, fontSize: '0.75rem' },
  unknown:      { backgroundColor: theme.palette.grey[500], color: '#fff', height: 22, fontSize: '0.75rem' },
}));

interface CapiCluster {
  metadata: { name: string; namespace: string; creationTimestamp?: string };
  spec?: {
    infrastructureRef?: { kind?: string };
    controlPlaneRef?: { name?: string };
    controlPlaneEndpoint?: { host?: string; port?: number };
    topology?: { version?: string };
  };
  status?: {
    phase?: string;
    controlPlaneReady?: boolean;
    infrastructureReady?: boolean;
  };
}

interface CapiClusterList {
  items: CapiCluster[];
}

interface ControlPlane {
  metadata: { name: string };
  spec?: { version?: string };
}

interface ControlPlaneList {
  items: ControlPlane[];
}

type ClusterRow = {
  name: string;
  phase: string;
  infraKind: string;
  k8sVersion: string;
  apiEndpoint: string;
  controlPlaneReady: boolean;
  infraReady: boolean;
  age: string;
};

function formatAge(timestamp?: string): string {
  if (!timestamp) return '—';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function buildVersionMap(list: ControlPlane[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const cp of list) {
    if (cp.metadata.name && cp.spec?.version) {
      m.set(cp.metadata.name, cp.spec.version);
    }
  }
  return m;
}

function parseCluster(
  c: CapiCluster,
  versionMap: Map<string, string>,
): ClusterRow {
  const host = c.spec?.controlPlaneEndpoint?.host ?? '';
  const port = c.spec?.controlPlaneEndpoint?.port;
  const cpName = c.spec?.controlPlaneRef?.name ?? '';
  const k8sVersion =
    c.spec?.topology?.version ??
    versionMap.get(cpName) ??
    '—';
  return {
    name: c.metadata.name,
    phase: c.status?.phase ?? 'Unknown',
    infraKind: c.spec?.infrastructureRef?.kind?.replace(/Cluster$/, '') ?? '—',
    k8sVersion,
    apiEndpoint: host ? `${host}${port ? `:${port}` : ''}` : '—',
    controlPlaneReady: c.status?.controlPlaneReady ?? false,
    infraReady: c.status?.infrastructureReady ?? false,
    age: formatAge(c.metadata.creationTimestamp),
  };
}

type PhaseClass = 'provisioned' | 'provisioning' | 'failed' | 'deleting' | 'unknown';

export const ManagedClustersTab = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const kubernetesApi = useApi(kubernetesApiRef);

  const clusterName =
    entity.metadata.annotations?.['backstage.io/kubernetes-id'] ?? entity.metadata.name;

  const [rows, setRows] = useState<ClusterRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const proxy = (path: string) => kubernetesApi.proxy({ clusterName, path });

      const [clustersResp, k3sResp, kubeadmResp] = await Promise.all([
        proxy('/apis/cluster.x-k8s.io/v1beta1/clusters'),
        proxy('/apis/controlplane.cluster.x-k8s.io/v1beta1/kthreescontrolplanes').catch(() => null),
        proxy('/apis/controlplane.cluster.x-k8s.io/v1beta1/kubeadmcontrolplanes').catch(() => null),
      ]);

      if (!clustersResp.ok) {
        throw new Error(`Kubernetes API returned ${clustersResp.status}`);
      }

      const versionMap = new Map<string, string>();

      if (k3sResp?.ok) {
        const body = (await k3sResp.json()) as ControlPlaneList;
        buildVersionMap(body.items ?? []).forEach((v, k) => versionMap.set(k, v));
      }
      if (kubeadmResp?.ok) {
        const body = (await kubeadmResp.json()) as ControlPlaneList;
        buildVersionMap(body.items ?? []).forEach((v, k) => versionMap.set(k, v));
      }

      const clusters = (await clustersResp.json()) as CapiClusterList;
      setRows((clusters.items ?? []).map(c => parseCluster(c, versionMap)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch managed clusters');
    } finally {
      setLoading(false);
    }
  }, [kubernetesApi, clusterName]);

  useEffect(() => {
    load();
  }, [load]);

  const phaseChipClass = (phase: string): string => {
    const key = phase.toLowerCase() as PhaseClass;
    return classes[key] ?? classes.unknown;
  };

  const columns: TableColumn<ClusterRow>[] = [
    {
      title: 'Name',
      field: 'name',
      render: r => (
        <Button
          color="primary"
          size="small"
          style={{ fontFamily: 'monospace', textTransform: 'none', padding: 0, minWidth: 0 }}
          onClick={() => setSelectedCluster(r.name)}
        >
          {r.name}
        </Button>
      ),
    },
    {
      title: 'Phase',
      field: 'phase',
      render: r => <Chip label={r.phase} className={phaseChipClass(r.phase)} />,
    },
    { title: 'Infrastructure', field: 'infraKind' },
    { title: 'K8s Version', field: 'k8sVersion' },
    {
      title: 'API Endpoint',
      field: 'apiEndpoint',
      render: r => <span style={{ fontFamily: 'monospace' }}>{r.apiEndpoint}</span>,
    },
    {
      title: 'Control Plane',
      field: 'controlPlaneReady',
      render: r => (
        <Chip
          label={r.controlPlaneReady ? 'Ready' : 'Not Ready'}
          className={r.controlPlaneReady ? classes.provisioned : classes.failed}
        />
      ),
    },
    {
      title: 'Infrastructure',
      field: 'infraReady',
      render: r => (
        <Chip
          label={r.infraReady ? 'Ready' : 'Not Ready'}
          className={r.infraReady ? classes.provisioned : classes.failed}
        />
      ),
    },
    { title: 'Age', field: 'age' },
  ];

  if (selectedCluster !== null) {
    return (
      <ManagedClusterDetailPanel
        mgmtClusterName={clusterName}
        capiClusterName={selectedCluster}
        onBack={() => setSelectedCluster(null)}
      />
    );
  }

  return (
    <Content>
      {error && <WarningPanel title="Failed to fetch managed clusters" message={error} />}
      <Table<ClusterRow>
        title={`Managed Clusters (${rows.length})`}
        isLoading={loading}
        data={rows}
        columns={columns}
        options={{ paging: false, search: false }}
      />
    </Content>
  );
};
